import {
    CloseOutlined,
    FileOutlined,
    LinkOutlined,
    PaperClipOutlined,
    SendOutlined,
} from '@ant-design/icons';
import { Button, Input, Spin, Tooltip, Typography, theme } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import type { ToolCallDto } from '@clh/api-client';
import { useConfig } from '@clh/ui';

import { useApiClient } from '../hooks/use-api-client';
import { useToast } from '../hooks/use-toast';

import { ChatMessages } from './chat-messages';
import humlaBotSrc from './humla-bot.png';
import type { Attachment, ChatMessage } from './types';

const { TextArea } = Input;

const { Text } = Typography;

const ACCEPTED_FILE_TYPES =
    'image/jpeg,image/png,image/webp,image/gif,application/pdf';
let messageIdCounter = 0;

interface ChatProps {
    chatToken: string | null;
    onConversationChange?: (conversationId: string) => void;
}

export default function Chat({ chatToken, onConversationChange }: ChatProps) {
    const config = useConfig();
    const api = useApiClient();
    const { notification } = useToast();
    const { conversationId: urlConversationId } = useParams<{
        conversationId: string;
    }>();

    const {
        token: { colorBgContainer, borderRadiusLG },
    } = theme.useToken();
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [input, setInput] = useState('');
    const [attachments, setAttachments] = useState<Attachment[]>([]);
    const [copied, setCopied] = useState(false);
    const [isStreaming, setIsStreaming] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [loading, setLoading] = useState(!!urlConversationId);
    const [conversationId, setConversationId] = useState<string | null>(
        urlConversationId ?? null
    );
    const [conversationTitle, setConversationTitle] = useState<string | null>(
        null
    );
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const abortControllerRef = useRef<AbortController | null>(null);
    const textAreaRef = useRef<ReturnType<typeof Input.TextArea> | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Load existing conversation from URL
    useEffect(() => {
        if (!urlConversationId || !api) return;
        let cancelled = false;

        async function load() {
            if (!api) return;
            const data = await api.adminChatControllerGetConversation({
                conversationId: urlConversationId!,
            });
            if (cancelled) return;

            setMessages(
                data.messages.map((m) => ({
                    ...m,
                    attachments: m.attachments?.map((a) => ({
                        mimeType: a.mimeType,
                        name: a.name ?? 'Attachment',
                    })),
                }))
            );
            setConversationId(data.id);
            if (data.title) setConversationTitle(data.title);
        }

        load()
            .catch((err) => {
                // biome-ignore lint/suspicious/noConsole: error logging
                console.error('[Chat] Failed to load conversation:', err);
                notification.error({
                    message: 'Failed to load conversation',
                    duration: 5,
                });
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [urlConversationId, api]);

    const scrollToBottom = useCallback(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, []);

    useEffect(() => {
        scrollToBottom();
    }, [messages, scrollToBottom]);

    useEffect(() => {
        requestAnimationFrame(() => {
            (textAreaRef.current as any)?.focus();
        });
    }, []);

    const addFiles = useCallback(
        async (files: FileList | File[]) => {
            const fileArray = Array.from(files);
            const validFiles = fileArray.filter((f) => {
                if (!ACCEPTED_FILE_TYPES.split(',').includes(f.type)) {
                    notification.error({
                        message: `Unsupported file type: ${f.type || f.name}. Supported: JPEG, PNG, WebP, GIF, PDF.`,
                        duration: 5,
                    });
                    return false;
                }
                return true;
            });

            if (!api) return;

            const results = await Promise.allSettled(
                validFiles.map(async (f) => {
                    const res = await api.adminChatControllerUploadFile({
                        file: f,
                    });
                    return {
                        url: res.url,
                        mimeType: res.mimeType,
                        name: res.name,
                    } as Attachment;
                })
            );
            const succeeded: Attachment[] = [];
            const failed: string[] = [];
            for (let i = 0; i < results.length; i++) {
                const result = results[i];
                if (result.status === 'fulfilled') {
                    succeeded.push(result.value);
                } else {
                    failed.push(validFiles[i].name);
                }
            }
            if (failed.length > 0) {
                notification.error({
                    message: `Failed to upload: ${failed.join(', ')}`,
                    duration: 5,
                });
            }
            if (succeeded.length > 0) {
                setAttachments((prev) => [...prev, ...succeeded]);
            }
        },
        [notification, api]
    );

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(true);
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            setIsDragging(false);
        }
    }, []);

    const handleDrop = useCallback(
        (e: React.DragEvent) => {
            e.preventDefault();
            setIsDragging(false);
            if (e.dataTransfer.files.length > 0) {
                addFiles(e.dataTransfer.files);
            }
        },
        [addFiles]
    );

    const removeAttachment = useCallback((index: number) => {
        setAttachments((prev) => prev.filter((_, i) => i !== index));
    }, []);

    const sendMessage = async () => {
        const trimmed = input.trim();
        if ((!trimmed && attachments.length === 0) || isStreaming || !chatToken)
            return;

        const currentAttachments = [...attachments];
        const userMessage: ChatMessage = {
            id: `user-${++messageIdCounter}`,
            role: 'user',
            content: trimmed,
            attachments:
                currentAttachments.length > 0 ? currentAttachments : undefined,
        };
        const updatedMessages = [...messages, userMessage];
        setMessages(updatedMessages);
        setInput('');
        setAttachments([]);
        setIsStreaming(true);

        const assistantMessage: ChatMessage = {
            id: `assistant-${++messageIdCounter}`,
            role: 'assistant',
            content: '',
            toolCalls: [],
        };
        const assistantIndex = updatedMessages.length;
        setMessages((prev) => [...prev, assistantMessage]);

        const abortController = new AbortController();
        abortControllerRef.current = abortController;

        try {
            const apiMessages = updatedMessages.map((m) => {
                // Build content as array if message has attachments
                if (m.attachments?.length) {
                    const contentBlocks: Array<
                        | { type: 'text'; text: string }
                        | {
                              type: 'image';
                              url: string;
                              mimeType: string;
                              name?: string;
                          }
                    > = [];
                    if (m.content) {
                        contentBlocks.push({ type: 'text', text: m.content });
                    }
                    for (const att of m.attachments) {
                        if (!att.url) continue; // Persisted metadata only — no URL to send
                        contentBlocks.push({
                            type: 'image',
                            url: att.url,
                            mimeType: att.mimeType,
                            name: att.name,
                        });
                    }
                    return { role: m.role, content: contentBlocks };
                }
                return { role: m.role, content: m.content };
            });

            const response = await fetch(`${config.apiBaseUrl}/admin-chat`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${chatToken}`,
                },
                body: JSON.stringify({
                    messages: apiMessages,
                    source: 'ADMIN_WEB',
                    ...(conversationId && { conversationId }),
                }),
                signal: abortController.signal,
            });

            if (!response.ok) {
                throw new Error(
                    `HTTP ${response.status}: ${response.statusText}`
                );
            }

            if (!response.body) {
                throw new Error(
                    'Server returned an empty response. Please try again.'
                );
            }
            const reader = response.body.getReader();

            const decoder = new TextDecoder();
            let buffer = '';

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });

                const parts = buffer.split('\n\n');
                buffer = parts.pop() ?? '';

                for (const part of parts) {
                    if (!part.trim()) continue;

                    const lines = part.split('\n');
                    let eventType = '';
                    let eventData = '';

                    for (const line of lines) {
                        if (line.startsWith('event: ')) {
                            eventType = line.slice(7);
                        } else if (line.startsWith('data: ')) {
                            eventData = line.slice(6);
                        }
                    }

                    if (!eventType || !eventData) continue;

                    let data: any;
                    try {
                        data = JSON.parse(eventData);
                    } catch (parseError) {
                        // biome-ignore lint/suspicious/noConsole: error logging
                        console.warn(
                            '[Chat] Failed to parse SSE event:',
                            eventData.slice(0, 200),
                            parseError
                        );
                        continue;
                    }

                    switch (eventType) {
                        case 'text_delta':
                            setMessages((prev) => {
                                const updated = [...prev];
                                const assistant = updated[assistantIndex];
                                if (assistant) {
                                    updated[assistantIndex] = {
                                        ...assistant,
                                        content: assistant.content + data.text,
                                    };
                                }
                                return updated;
                            });
                            break;

                        case 'tool_call_start':
                            setMessages((prev) => {
                                const updated = [...prev];
                                const assistant = updated[assistantIndex];
                                if (assistant) {
                                    updated[assistantIndex] = {
                                        ...assistant,
                                        toolCalls: [
                                            ...(assistant.toolCalls ?? []),
                                            {
                                                id: data.id,
                                                name: data.name,
                                                input: data.input,
                                            },
                                        ],
                                    };
                                }
                                return updated;
                            });
                            break;

                        case 'tool_call_result':
                            setMessages((prev) => {
                                const updated = [...prev];
                                const assistant = updated[assistantIndex];
                                if (assistant?.toolCalls) {
                                    const calls = [
                                        ...assistant.toolCalls,
                                    ] as ToolCallDto[];
                                    const lastCallOfName = calls
                                        .slice()
                                        .reverse()
                                        .find((tc) => tc.name === data.name);
                                    if (lastCallOfName) {
                                        lastCallOfName.result = data.result;
                                        if (data.imageUrl) {
                                            lastCallOfName.imageUrl =
                                                data.imageUrl;
                                        }
                                        if (data.imageMimeType) {
                                            lastCallOfName.imageMimeType =
                                                data.imageMimeType;
                                        }
                                    }
                                    updated[assistantIndex] = {
                                        ...assistant,
                                        toolCalls: calls,
                                    };
                                }
                                return updated;
                            });
                            break;

                        case 'done':
                            if (data.conversationId) {
                                setConversationId(data.conversationId);
                                window.history.replaceState(
                                    null,
                                    '',
                                    `/chat/${data.conversationId}`
                                );
                                onConversationChange?.(data.conversationId);
                            }
                            if (data.title) {
                                setConversationTitle(data.title);
                            }
                            break;

                        case 'warning':
                            setMessages((prev) => {
                                const updated = [...prev];
                                const assistant = updated[assistantIndex];
                                if (assistant) {
                                    updated[assistantIndex] = {
                                        ...assistant,
                                        content:
                                            assistant.content +
                                            `\n\n_${data.message}_`,
                                    };
                                }
                                return updated;
                            });
                            break;

                        case 'error':
                            setMessages((prev) => {
                                const updated = [...prev];
                                const assistant = updated[assistantIndex];
                                if (assistant) {
                                    updated[assistantIndex] = {
                                        ...assistant,
                                        content:
                                            assistant.content +
                                            `\n\nError: ${data.message}`,
                                    };
                                }
                                return updated;
                            });
                            break;
                    }
                }
            }
        } catch (error) {
            if ((error as Error).name !== 'AbortError') {
                const errorMessage =
                    error instanceof Error ? error.message : 'Unknown error';
                setMessages((prev) => {
                    const updated = [...prev];
                    const assistant = updated[assistantIndex];
                    if (assistant) {
                        updated[assistantIndex] = {
                            ...assistant,
                            content:
                                assistant.content +
                                `\n\nError: ${errorMessage}`,
                        };
                    }
                    return updated;
                });
            }
        } finally {
            setIsStreaming(false);
            abortControllerRef.current = null;
            requestAnimationFrame(() => {
                (textAreaRef.current as any)?.focus();
            });
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    if (loading) {
        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                }}
            >
                <Spin size="large" />
            </div>
        );
    }

    return (
        <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            style={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                width: '100%',
                background: '#f0f2f5',
                position: 'relative',
            }}
        >
            {/* Drag overlay */}
            {isDragging && (
                <div
                    style={{
                        position: 'absolute',
                        inset: 0,
                        background: 'rgba(22, 119, 255, 0.08)',
                        border: '2px dashed #1677ff',
                        borderRadius: 8,
                        zIndex: 10,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        pointerEvents: 'none',
                    }}
                >
                    <Text style={{ fontSize: 18, color: '#1677ff' }}>
                        Drop files here
                    </Text>
                </div>
            )}

            <div
                style={{
                    padding: '12px 24px',
                    borderBottom: '1px solid #e8e8e8',
                    background: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
                <Typography.Title level={4} style={{ margin: 0 }}>
                    {conversationTitle ?? 'New Conversation'}
                </Typography.Title>
                {conversationId && (
                    <Tooltip title={copied ? 'Copied!' : 'Copy share link'}>
                        <Button
                            icon={<LinkOutlined />}
                            onClick={() => {
                                navigator.clipboard
                                    .writeText(window.location.href)
                                    .then(() => {
                                        setCopied(true);
                                        setTimeout(
                                            () => setCopied(false),
                                            2000
                                        );
                                    })
                                    .catch(() => {
                                        notification.error({
                                            message:
                                                'Failed to copy link to clipboard',
                                            duration: 3,
                                        });
                                    });
                            }}
                        />
                    </Tooltip>
                )}
            </div>
            {/* Messages area */}
            <div
                style={{
                    flex: 1,
                    overflowY: 'auto',
                    padding: '24px 24px 0',
                }}
            >
                <div style={{ maxWidth: 800, margin: '0 auto' }}>
                    {messages.length === 0 && (
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: 'calc(100vh - 190px)',
                                gap: 12,
                            }}
                        >
                            <img
                                src={humlaBotSrc}
                                alt="Humla Bot"
                                style={{
                                    width: 80,
                                    height: 80,
                                    borderRadius: '50%',
                                }}
                            />
                            <Text type="secondary" style={{ fontSize: 16 }}>
                                Ask a question about Humla platform data
                            </Text>
                            <Text type="secondary" style={{ fontSize: 13 }}>
                                e.g. "How many active profiles are there?" or
                                "Show me upcoming shifts"
                            </Text>
                        </div>
                    )}

                    <ChatMessages
                        messages={messages}
                        isStreaming={isStreaming}
                    />
                    <div ref={messagesEndRef} />
                </div>
            </div>

            {/* Input area */}
            <div
                style={{
                    padding: '16px 24px 24px',
                    background: '#f0f2f5',
                }}
            >
                <div
                    style={{
                        maxWidth: 800,
                        margin: '0 auto',
                    }}
                >
                    {/* Attachment previews */}
                    {attachments.length > 0 && (
                        <div
                            style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 8,
                                marginBottom: 8,
                                padding: 8,
                                background: colorBgContainer,
                                borderRadius: borderRadiusLG,
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                            }}
                        >
                            {attachments.map((att, idx) => (
                                <div
                                    key={`${att.name}-${idx}`}
                                    style={{
                                        position: 'relative',
                                        display: 'inline-flex',
                                    }}
                                >
                                    {att.mimeType === 'application/pdf' ? (
                                        <div
                                            onClick={() =>
                                                att.url &&
                                                window.open(att.url, '_blank')
                                            }
                                            style={{
                                                width: 80,
                                                height: 60,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                background: '#f5f5f5',
                                                borderRadius: 6,
                                                border: '1px solid #d9d9d9',
                                                gap: 2,
                                                cursor: 'pointer',
                                            }}
                                        >
                                            <FileOutlined
                                                style={{ fontSize: 18 }}
                                            />
                                            <Text
                                                style={{
                                                    fontSize: 9,
                                                    maxWidth: 70,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {att.name}
                                            </Text>
                                        </div>
                                    ) : (
                                        <img
                                            src={att.url}
                                            alt={att.name}
                                            onClick={() =>
                                                att.url &&
                                                window.open(att.url, '_blank')
                                            }
                                            style={{
                                                width: 80,
                                                height: 60,
                                                objectFit: 'cover',
                                                borderRadius: 6,
                                                border: '1px solid #d9d9d9',
                                                cursor: 'pointer',
                                            }}
                                        />
                                    )}
                                    <Button
                                        size="small"
                                        type="text"
                                        icon={<CloseOutlined />}
                                        onClick={() => removeAttachment(idx)}
                                        style={{
                                            position: 'absolute',
                                            top: -6,
                                            right: -6,
                                            width: 18,
                                            height: 18,
                                            minWidth: 18,
                                            padding: 0,
                                            fontSize: 10,
                                            background: '#fff',
                                            borderRadius: '50%',
                                            boxShadow:
                                                '0 1px 3px rgba(0,0,0,0.2)',
                                        }}
                                    />
                                </div>
                            ))}
                        </div>
                    )}

                    <div
                        style={{
                            display: 'flex',
                            gap: 8,
                            background: colorBgContainer,
                            padding: 8,
                            borderRadius: borderRadiusLG,
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                        }}
                    >
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept={ACCEPTED_FILE_TYPES}
                            multiple
                            style={{ display: 'none' }}
                            onChange={(e) => {
                                if (e.target.files) {
                                    addFiles(e.target.files);
                                    e.target.value = '';
                                }
                            }}
                        />
                        <Tooltip title="Attach image or PDF">
                            <Button
                                icon={<PaperClipOutlined />}
                                onClick={() => fileInputRef.current?.click()}
                                style={{ alignSelf: 'flex-end' }}
                            />
                        </Tooltip>
                        <TextArea
                            ref={textAreaRef as any}
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="Ask about profiles, shifts, facilities..."
                            autoSize={{ minRows: 1, maxRows: 4 }}
                            variant="borderless"
                            style={{ flex: 1, resize: 'none' }}
                        />
                        <Button
                            type="primary"
                            icon={<SendOutlined />}
                            onClick={sendMessage}
                            disabled={
                                isStreaming ||
                                (!input.trim() && attachments.length === 0)
                            }
                            style={{ alignSelf: 'flex-end' }}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}
